/*
  Widget Loader
  - Declarative widget registration with selectors and optional options.
  - Avoids double-initialization via WeakMap memoization and data attribute guards.
  - Handles dynamic nodes via an optional observe() that can be called after AJAX/DOM updates.
*/

const _instances = new WeakMap();

export class WidgetLoader {
  constructor(registry = []) {
    this.registry = registry;
  }

  // Initialize all registered widgets on current document (or within a root)
  init(root = document) {
    this.registry.forEach(({ selector, setup }) => {
      const nodes = root.querySelectorAll(selector);
      nodes.forEach((el) => this.#initNode(el, setup));
    });
  }

  // Re-run on a specific root (e.g., when content is swapped)
  observe(root) {
    this.init(root);
  }

  // Private: initialize a node safely once
  #initNode(el, setup) {
    if (!el || typeof setup !== 'function') return;

    if (_instances.has(el)) return;

    const marker = el.getAttribute('data-widget-marker') || 'data-widget-initialized';
    if (el.hasAttribute(marker)) return;

    const instance = setup(el);

    if (instance && typeof instance.init === 'function') {
      instance.init();
    }

    el.setAttribute(marker, '');
    _instances.set(el, instance || true);
  }
}

// Helper to build registry entries from class-based widgets
export const asClassWidget = (Selector, WidgetClass, options = {}) => ({
  selector: Selector,
  setup: (el) => new WidgetClass(el, options)
});
